import { Dispatch, FC, useCallback, useEffect } from 'react';
import { usePreloadSlot } from '../../UnicronApp/preloader/PreloadSlot';
import { useDesktopTopNav } from '../../common/Experiments/DesktopTopNavExperiment';
import { useHomepageTiles } from '../../common/Experiments/HomepageTilesExperiment';
import { SearchBar } from '../../common/components/SearchHeader/SearchBar/SearchBar';
import { SearchPromotionItem } from '../../common/components/SearchHeader/Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { Suggestions } from '../../common/components/SearchHeader/Suggestions/Suggestions';
import { SuggestionData } from '../../common/components/SearchHeader/Suggestions/types';
import { useSuggestionHandler } from '../../common/components/SearchHeader/useSuggestionHandler';
import { useOnClickOutside } from '../../common/hooks/useOnClickOutside';
import { renderQuery, useSearch } from '../../common/hooks/useSearch';
import { PromotedSpecialtiesSlot } from '../preloadPromotedSpecialties';
import { transformPromotedSpecialtiesForTopNavExp } from '../transformPromotedSpecialtiesForTopNavExp';
import { InlineSearchContainer } from './InlineSearch.styles';
import { SearchSpecialtySelector } from './SearchSpecialtySelector/SearchSpecialtySelector';

export interface InlineSearchProps {
  onSetSearchSpecialty?: Dispatch<SearchPromotionItem | null>;
  searchSpecialty?: SearchPromotionItem | null;
  disableSearchBar?: boolean;
  renderGPCards?: boolean;
  renderOtherTiles?: boolean;
}

export const InlineSearch: FC<InlineSearchProps> = ({
  onSetSearchSpecialty,
  searchSpecialty,
  disableSearchBar,
  renderGPCards,
  renderOtherTiles,
}) => {
  const {
    focusInlineSearch,
    setFocusInlineSearch,
    inlineSearchText,
    setInlineSearchText,
    inlineSearchInputRef,
    inlineSearchSuggestionsRef,
    activeSpecialtyIndex,
    setActiveSpecialtyIndex,
  } = useSearch();

  const topNavExperiment = useDesktopTopNav();

  const preloadedPromotedSpecialties = usePreloadSlot(PromotedSpecialtiesSlot);

  const promotedSpecialties: SearchPromotionItem[] = topNavExperiment
    ? transformPromotedSpecialtiesForTopNavExp(preloadedPromotedSpecialties)
    : preloadedPromotedSpecialties;

  // const location = useClientLocation();
  const runSearchFromSuggestion = useSuggestionHandler();

  const onClear = useCallback(() => {
    setInlineSearchText('');
    setActiveSpecialtyIndex(-1);
    setFocusInlineSearch(false);
    onSetSearchSpecialty?.(null);
  }, [onSetSearchSpecialty, setActiveSpecialtyIndex, setFocusInlineSearch, setInlineSearchText]);

  useEffect(() => {
    searchSpecialty ?? onClear();
  }, [onClear, searchSpecialty]);

  const handleSuggestion = useCallback(
    (suggestion: SuggestionData) => {
      setFocusInlineSearch(false);
      runSearchFromSuggestion(suggestion);
    },
    [runSearchFromSuggestion, setFocusInlineSearch],
  );

  const onClickSearch = useCallback(() => {
    handleSuggestion({
      type: 'TextSuggestion',
      filters: { text: inlineSearchText ?? '' },
      id: `text-${inlineSearchText}`,
    });
  }, [handleSuggestion, inlineSearchText]);

  const onSpecialtyChange = (specialty: SearchPromotionItem | null) => {
    if (!specialty) onClear();
    else {
      setInlineSearchText(
        renderQuery({
          topic: {
            treatment: specialty?.title ?? '',
          },
        }),
      );

      onSetSearchSpecialty?.(specialty);
      setFocusInlineSearch(true);
    }
  };

  useOnClickOutside(inlineSearchSuggestionsRef, () => setFocusInlineSearch(false));

  const homepageTilesExperiment = useHomepageTiles();

  return (
    <InlineSearchContainer experiment={homepageTilesExperiment}>
      {!!promotedSpecialties.length && (
        <SearchSpecialtySelector
          promotedSpecialties={promotedSpecialties}
          onSpecialtyChange={onSpecialtyChange}
          activeSpecialtyIndex={activeSpecialtyIndex}
          setActiveSpecialtyIndex={setActiveSpecialtyIndex}
          renderGPCards={renderGPCards}
          renderOtherTiles={renderOtherTiles}
        />
      )}
      {!disableSearchBar && (
        <SearchBar
          placeholder="Search for a Specialty, Practice or Practitioner name"
          focused={focusInlineSearch}
          setFocused={setFocusInlineSearch}
          value={inlineSearchText}
          onChange={setInlineSearchText}
          onClickSearch={onClickSearch}
          onClear={onClear}
          inputRef={inlineSearchInputRef}
          inline
        />
      )}
      {focusInlineSearch && (
        <Suggestions
          searchTerm={inlineSearchText}
          onChoose={handleSuggestion}
          ref={inlineSearchSuggestionsRef}
          disableTabs
          inline
        />
      )}
    </InlineSearchContainer>
  );
};
